import React from "react";
import { toast } from "react-hot-toast";

import FleetService from "@/services/FleetService";

export interface MaintenanceFormData {
  cost: number;
  date: string;
  maintenanceType: string;
  notes: string;
  odometer: number;
  serviceProvider: string;
  status: "completed" | "in-progress" | "pending";
  vehicle: string;
}

interface AddMaintenanceModalProps {
  onClose: () => void;
  onSubmit: (data: MaintenanceFormData) => void;
  open: boolean;
  vehicles?: Array<{ id: string; label: string }>;
}

const AddMaintenanceModal: React.FC<AddMaintenanceModalProps> = ({
  onClose,
  onSubmit,
  open,
  vehicles = [],
}) => {
  const [form, setForm] = React.useState<MaintenanceFormData>({
    cost: 0,
    date: "",
    maintenanceType: "",
    notes: "",
    odometer: 0,
    serviceProvider: "",
    status: "pending",
    vehicle: "",
  });
  const [submitting, setSubmitting] = React.useState(false);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-gray-900 md:max-w-3xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "90vh", overflowY: "auto", width: "80vw" }}
      >
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          Add Maintenance
        </h2>
        <form
          className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2"
          onSubmit={async (e) => {
            e.preventDefault();
            setSubmitting(true);
            try {
              const {
                cost,
                date,
                maintenanceType,
                notes,
                odometer,
                serviceProvider,
                status,
                vehicle,
              } = form;
              const payload = {
                cost,
                date,
                maintenance_type: maintenanceType,
                notes,
                odometer,
                service_provider: serviceProvider,
                status,
                vehicle_id: vehicle,
              };
              await FleetService.createMaintenance(payload);
              toast.success("Maintenance added successfully!");
              onSubmit(form);
              setForm({
                cost: 0,
                date: "",
                maintenanceType: "",
                notes: "",
                odometer: 0,
                serviceProvider: "",
                status: "pending",
                vehicle: "",
              });
              onClose();
            } catch (error: any) {
              if (error.response && error.response.data) {
                toast.error(
                  error.response.data.message +
                    "\n" +
                    Object.entries(error.response.data.errors || {})
                      .map(
                        ([field, msgs]) =>
                          `${field}: ${
                            Array.isArray(msgs) ? msgs.join(", ") : msgs
                          }`,
                      )
                      .join("\n"),
                );
              } else {
                toast.error("Failed to add maintenance.");
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {/* Vehicle Dropdown */}
          <div className="relative">
            <select
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="maintenance-vehicle"
              onChange={(e) => setForm({ ...form, vehicle: e.target.value })}
              required
              value={form.vehicle}
            >
              <option value="">Select Vehicle</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label}
                </option>
              ))}
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="maintenance-vehicle"
            >
              Vehicle
            </label>
          </div>

          {/* Maintenance Type */}
          <div className="relative">
            <input
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="maintenance-type"
              onChange={(e) =>
                setForm({ ...form, maintenanceType: e.target.value })
              }
              required
              value={form.maintenanceType}
            />
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="maintenance-type"
            >
              Maintenance Type
            </label>
          </div>

          {/* Status */}
          <div className="relative">
            <select
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="maintenance-status"
              onChange={(e) =>
                setForm({
                  ...form,
                  status: e.target.value as MaintenanceFormData["status"],
                })
              }
              required
              value={form.status}
            >
              <option value="pending">Pending</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="maintenance-status"
            >
              Status
            </label>
          </div>

          {/* Date */}
          <div className="relative">
            <input
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="maintenance-date"
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              required
              type="date"
              value={form.date}
            />
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="maintenance-date"
            >
              Date
            </label>
          </div>

          {/* Odometer */}
          <div className="relative">
            <input
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="maintenance-odometer"
              min={0}
              onChange={(e) =>
                setForm({ ...form, odometer: Number(e.target.value) })
              }
              required
              type="number"
              value={form.odometer}
            />
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="maintenance-odometer"
            >
              Odometer
            </label>
          </div>

          {/* Service Provider */}
          <div className="relative">
            <input
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="maintenance-service-provider"
              onChange={(e) =>
                setForm({ ...form, serviceProvider: e.target.value })
              }
              required
              value={form.serviceProvider}
            />
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="maintenance-service-provider"
            >
              Service Provider
            </label>
          </div>

          {/* Cost */}
          <div className="relative">
            <input
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="maintenance-cost"
              min={0}
              onChange={(e) =>
                setForm({ ...form, cost: Number(e.target.value) })
              }
              type="number"
              value={form.cost}
            />
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="maintenance-cost"
            >
              Cost
            </label>
          </div>

          {/* Notes */}
          <div className="relative md:col-span-2">
            <textarea
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="maintenance-notes"
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              value={form.notes}
            />
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="maintenance-notes"
            >
              Notes
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2 md:col-span-2">
            <button
              className="rounded bg-gray-200 px-4 py-2 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className="rounded bg-blue-600 px-4 py-2 text-white dark:bg-blue-500"
              disabled={submitting}
              type="submit"
            >
              {submitting ? "Adding..." : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMaintenanceModal;
